import { LEGACY_CUSTOMER, offered } from './i18n/customer'
import { useSession } from '../ui/Session'
import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from './api'
import { fromSnapshot, type BranchState } from './branchState'
import { rememberBranch, rememberedBranch, startingBranch } from './branchChoice'
import { LiveConnection, type ConnectionStatus } from './live'
import { contextOrganisation } from './ownerOrg'
import { pageForUnit } from './screenKind'
import { keepState, keptStateKey, readKeptState } from './screenKeep'
import { announceIfSignedOut } from './sessionEnded'
import { explain } from './staffErrors'
import { t, type StaffLanguage } from './staffText'
import type { Announcement, Branch, Service, TabletButton, Unit } from './types'
import { keyRefusedAfter, retiredAfter } from './units'

/** What a machine reports it has drawn. See LiveConnection.report. */
export type Showing = { layoutId?: string; version: number; build?: string }

// Every screen needs the same three things: which branch it is looking at, that
// branch's live state, and whether the link to the server is up. This hook is all
// three, so a page is only the part that is different about it.

export interface BranchLive {
  branch: Branch | null
  branches: Branch[]
  /** The machine this screen is, when its address named one. */
  unit: Unit | null
  /** A kiosk's menu, when this screen was reached by address. Empty otherwise —
   *  an operator's page loads its own. */
  services: Service[]
  /** A feedback tablet's buttons, when this screen was reached by address; empty
   *  otherwise. */
  marks: TabletButton[]
  /** The organisation's customer languages, in order; the first is the default. */
  languages: string[]
  /** A feedback tablet's press token, from the live connection that is open now;
   *  '' before one arrives and after that connection goes. Held in memory only. */
  pressToken: string
  state: BranchState | null
  status: ConnectionStatus
  announcement: Announcement | null
  error: string
  /** The person's browser was offered no branch at all; nothing connects. */
  noBranch: boolean
  chooseBranch: (id: string) => void
  /** Set when this screen's machine is already running somewhere else: the words
   *  to show, and the way to take it anyway. */
  inUse: string
  takeOver: () => void
  /** Counts the live channel's "ask for the design again" nudges, and reconnections after a drop
   *  (a nudge sent meanwhile was lost); useLayout reloads when it changes. */
  layoutNudge: number
  /** Set when the server says this screen's machine, or its branch, has been taken
   *  out of service. Only for a screen reached by address; cleared by the next
   *  answer, so a machine put back into service recovers by itself. */
  retired: boolean
  /** An addressed screen refused for bringing no key, or not its current one. */
  keyRefused: boolean
  /** Ask again, now, what this machine is — its services, its settings, and whether
   *  the branch is open. Does nothing for a screen that was not reached by address:
   *  a person's browser learns those from endpoints it can reach itself. */
  reidentify: () => void
  /** Ask the live channel for a fresh snapshot, now. See LiveConnection.resync. */
  resync: () => void
  /** Drop the live channel and make it again. See LiveConnection.reconnect. */
  reconnect: () => void
  /** Tell the server what this machine has drawn. See LiveConnection.report. */
  report: (showing: Showing) => void
  /** Tell the server what this machine's printer is doing. See LiveConnection.reportPrinter. */
  reportPrinter: (state: PrinterReportState, error?: string) => void
}

export type PrinterReportState = 'ready' | 'faulty' | 'absent'


/**
 * options names the machine, when this screen is one.
 *
 * An unattended screen is addressed by URL — /display/BAKU-01/TV1 — and the two
 * codes in it are resolved here: the branch by its code, then the unit by its own.
 * Both are codes rather than identifiers because the URL is read aloud over a
 * telephone and typed into a kiosk's autostart by somebody who will never see a
 * UUID.
 */
export interface BranchLiveOptions {
  branchCode?: string
  unitCode?: string
  /** The language error is said in: a person's screen passes its reader's. A
   *  machine's, reached by address, reads English, which is what it said before. */
  lang?: StaffLanguage
  /** Called once for every call the live channel delivers, straight from the
   *  socket. `announcement` is state, and React may merge two quick updates into
   *  one render — so a screen reacting to it in an effect can miss the first of
   *  two calls a second apart. The hall board's voice and call page must not. */
  onAnnounce?: (announce: Announcement) => void
}

/** What went wrong finding the branch when the server did not say: nothing it
 *  sent, so no error to put in words, only the fact. */
const NO_BRANCH = Symbol('no branch')
/** How often a hall board may write the queue it keeps for a reload offline. */
const KEEP_EVERY_MS = 3000

function storage(): Storage | undefined {
  try {
    return window.localStorage
  } catch {
    return undefined
  }
}

/** The branch list the last staff page was given, so the next page draws its picker at once. */
let lastBranches: { key: string; branches: Branch[] } | null = null

export function useBranchLive(label: string, options: BranchLiveOptions = {}): BranchLive {
  const session = useSession()
  const cacheKey = `${session?.identity.staff.id ?? ''}|${contextOrganisation()}`
  const cached = !(options.branchCode && options.unitCode) && lastBranches?.key === cacheKey ? lastBranches.branches : []
  const [branches, setBranches] = useState<Branch[]>(cached)
  const [branchId, setBranchId] = useState<string>(() => (cached.length > 0 ? startingBranch(cached, rememberedBranch()) : ''))
  const [state, setState] = useState<BranchState | null>(null)
  const [status, setStatus] = useState<ConnectionStatus>('connecting')
  const [announcement, setAnnouncement] = useState<Announcement | null>(null)
  // The failure itself rather than its sentence, so the sentence is made at render
  // in whatever language the screen is in now, not the one it was in when it failed.
  const [fault, setError] = useState<unknown>(null)
  const [unit, setUnit] = useState<Unit | null>(null)
  const [screenBranch, setScreenBranch] = useState<Branch | null>(null)
  const [services, setServices] = useState<Service[]>([])
  const [screenLanguages, setScreenLanguages] = useState<string[] | null>(null)
  const [marks, setMarks] = useState<TabletButton[]>([])
  const [pressToken, setPressToken] = useState('')
  const [inUse, setInUse] = useState('')
  const [retired, setRetired] = useState(false)
  const [keyRefused, setKeyRefused] = useState(false)
  const [layoutNudge, setLayoutNudge] = useState(0)
  const connection = useRef<LiveConnection | null>(null)
  // The last report, handed to every connection made after it: a branch resolved
  // a moment late makes a new connection, and the report must not stay behind on
  // the old one.
  const showing = useRef<Showing | null>(null)
  const printer = useRef<{ state: PrinterReportState; error?: string } | null>(null)
  // The latest callback, read at the moment a call arrives. A ref rather than a
  // dependency of the connection effect: a new function on every render would
  // tear the socket down and open it again each time.
  const onAnnounceRef = useRef(options.onAnnounce)
  useEffect(() => {
    onAnnounceRef.current = options.onAnnounce
  })

  const { branchCode, unitCode } = options
  // Only a hall board starts from the last queue it drew; the socket's first snapshot replaces it.
  const keptKey = label === 'display' && branchCode && unitCode ? keptStateKey(branchCode, unitCode) : ''
  const zone = screenBranch?.timezone
  useEffect(() => {
    if (!keptKey || zone === undefined) return
    const kept = readKeptState(storage(), keptKey, Date.now(), zone)
    if (kept) setState((current) => current ?? kept)
  }, [keptKey, zone])
  // At most one write per KEEP_EVERY_MS, always of the latest queue, so a busy hall cannot starve it.
  const latest = useRef<BranchState | null>(null)
  const keepTimer = useRef<number | undefined>(undefined)
  useEffect(() => {
    latest.current = state
    if (!keptKey || !state || keepTimer.current !== undefined) return
    keepTimer.current = window.setTimeout(() => {
      keepTimer.current = undefined
      if (latest.current) keepState(storage(), keptKey, latest.current, Date.now())
    }, KEEP_EVERY_MS)
  }, [keptKey, state])
  useEffect(() => () => window.clearTimeout(keepTimer.current), [])

  // An addressed screen: one call, no session, and the address settles everything.
  //
  // This is the whole of how a hall board or a kiosk starts. It does not ask which
  // branches it may reach, because it has no account to ask on behalf of; it does
  // not choose a branch, because its address chose one. A machine switched on cold
  // reaches its first paint in one round trip.
  const addressed = Boolean(branchCode && unitCode)
  // Asked again on a timer, not only when the machine is switched on.
  //
  // This call is where an addressed screen learns everything about itself that is
  // not the queue: its name, its services, its voice and printer settings, and
  // whether the branch is open. It used to be made exactly once, so a kiosk
  // switched on in the morning still believed the branch was open at midnight, and
  // a service added at head office never appeared on it until somebody power-cycled
  // the machine in the lobby. A minute is the same cadence the closed check used
  // before it was moved here, and these are all facts that change a few times a
  // year.
  const [identifiedAt, setIdentifiedAt] = useState(0)
  useEffect(() => {
    if (!branchCode || !unitCode) return
    let cancelled = false

    const identify = () => {
      api
        .screen(branchCode, unitCode)
        .then((view) => {
          if (cancelled) return
          // A machine whose kind was changed on the Machines page belongs on another
          // page now: a board turned into a feedback tablet would otherwise go on
          // drawing a board with a tablet's settings until somebody walked to it.
          // replace() rather than assign(), so Back does not return to the old page
          // only to be sent on again.
          const to = pageForUnit(label, view.unit?.kind, branchCode, unitCode)
          if (to !== null) {
            cancelled = true
            window.location.replace(to)
            return
          }
          setScreenBranch({
            id: view.branch.id,
            tenantId: '',
            code: view.branch.code,
            name: view.branch.name,
            timezone: view.branch.timezone ?? '',
            active: true,
            openNow: view.branch.openNow,
            opensAt: view.branch.opensAt,
            closesAt: view.branch.closesAt,
          } as Branch)
          setUnit(view.unit)
          setServices(view.services ?? [])
          // A server older than 0139 sends none: the three every screen offered then.
          setScreenLanguages(offered(view.languages ?? LEGACY_CUSTOMER))
          setMarks(view.marks ?? [])
          setBranchId(view.branch.id)
          setRetired(false)
          setKeyRefused(false)
        })
        // A screen that cannot reach the server keeps what it had rather than
        // blanking: the live channel already says the link is down. A machine
        // taken out of service is told so here, and must say so rather than sit
        // showing its last queue or "not connected" — the Machines page promises
        // that a retired machine's screen says it is retired.
        .catch((err: unknown) => {
          if (cancelled) return
          setError(err)
          setRetired((was) => retiredAfter(was, err))
          setKeyRefused((was) => keyRefusedAfter(was, err))
        })
    }

    identify()
    const timer = window.setInterval(identify, 60_000)
    return () => {
      cancelled = true
      window.clearInterval(timer)
    }
  }, [branchCode, unitCode, identifiedAt, label])

  // A person's browser: ask what they may reach, and remember what they chose.
  useEffect(() => {
    if (addressed) return
    api
      .branches()
      .then(({ branches: all }) => {
        // The installation owner sees every organisation's; the chosen one narrows them.
        const org = contextOrganisation()
        const branches = org ? all.filter((b) => b.tenantId === org) : all
        lastBranches = { key: cacheKey, branches }
        setBranches(branches)
        // An operator pinned to one branch is given it without being asked. Asking
        // would be a question with one possible answer, every morning.
        const chosen = startingBranch(branches, rememberedBranch())
        if (chosen) setBranchId(chosen)
        else setError(NO_BRANCH)
      })
      .catch((err: unknown) => setError(err))
  }, [addressed, cacheKey])

  // The connection hangs on which machine this is, never on the object that says
  // so. The identity poll above sets a fresh unit every minute, equal in every
  // field; with the object itself in the dependencies below, every kiosk and hall
  // board dropped its socket and opened a new one once a minute, for nothing.
  const unitId = unit?.id ?? ''
  useEffect(() => {
    if (!branchId) return
    // A screen that is a machine waits for its machine to be known before it
    // connects: the unit is claimed on the subscription, and connecting without it
    // would take the queue first and the claim second.
    if (unitCode && !unitId) return

    setInUse('')

    // The first paint comes from the HTTP snapshot rather than waiting for the
    // socket: a screen that shows the queue a moment sooner is worth one request.
    //
    // An addressed screen skips it. Its snapshot has no route of its own — the
    // socket delivers one on connect — and a second unauthenticated read endpoint
    // would be a second thing to keep redacted.
    let cancelled = false
    if (!addressed) {
      api
        .snapshot(branchId)
        .then((snapshot) => {
          if (cancelled) return
          setState((current) =>
            current && current.version >= snapshot.version ? current : fromSnapshot(snapshot),
          )
        })
        .catch(() => undefined)
    }

    // Per connection, so a socket made again after the branch or machine changed
    // does not inherit the old one's last word.
    let lastStatus: ConnectionStatus = 'connecting'
    let drops = 0
    const live = new LiveConnection(
      branchId,
      label,
      {
        onState: setState,
        onStatus: (next) => {
          // The server drops an addressed screen's socket when its machine is
          // redefined (Hub.UnitRedefined), and a kind change is one. Asking what
          // this machine is at that moment, rather than at the next minute's poll,
          // moves a board turned into a tablet to its new page within seconds.
          if (addressed && lastStatus === 'live' && next === 'reconnecting') {
            setIdentifiedAt((n) => n + 1)
          }
          if (addressed && lastStatus === 'reconnecting' && next === 'live') setLayoutNudge((n) => n + 1)
          // A staff page whose session ended would otherwise retry forever.
          if (!addressed) {
            if (next === 'live') drops = 0
            else if (next === 'reconnecting' && ++drops % 3 === 0) {
              void announceIfSignedOut(api.me, (detail) => window.dispatchEvent(new CustomEvent('qms:signed-out', { detail })))
            }
          }
          lastStatus = next
          setStatus(next)
        },
        onAnnounce: (announce) => {
          if (!announce) return
          setAnnouncement({ ...announce })
          onAnnounceRef.current?.({ ...announce })
        },
        onInUse: setInUse,
        onPressToken: setPressToken,
        onLayout: () => setLayoutNudge((n) => n + 1),
      },
      addressed
        ? { screen: { branchCode: branchCode!, unitCode: unitCode! } }
        : {},
    )
    connection.current = live
    if (showing.current) live.report(showing.current)
    if (printer.current) live.reportPrinter(printer.current.state, printer.current.error)
    live.start()

    return () => {
      cancelled = true
      live.stop()
      connection.current = null
    }
  }, [addressed, branchCode, branchId, label, unitId, unitCode])

  // Stable for the life of the screen. A page hands it to things it builds once —
  // the host a screen app is served, the effects that watch the menu's version —
  // and a new function every render would rebuild them every render.
  const reidentify = useCallback(() => setIdentifiedAt((n) => n + 1), [])
  const report = useCallback((next: Showing) => {
    showing.current = next
    connection.current?.report(next)
  }, [])
  const reportPrinter = useCallback((state: PrinterReportState, error?: string) => {
    printer.current = { state, error }
    connection.current?.reportPrinter(state, error)
  }, [])

  return {
    branch: screenBranch ?? branches.find((b) => b.id === branchId) ?? null,
    branches,
    unit,
    services,
    marks,
    languages: screenLanguages ?? offered(session?.identity.languages?.customer ?? LEGACY_CUSTOMER),
    pressToken,
    state,
    status,
    announcement,
    error: errorText(fault, options.lang ?? 'en'),
    noBranch: fault === NO_BRANCH,
    inUse,
    retired,
    keyRefused,
    layoutNudge,
    takeOver: () => {
      setInUse('')
      connection.current?.takeOver()
    },
    chooseBranch: (id: string) => {
      rememberBranch(id)
      setState(null)
      setBranchId(id)
    },
    /** Ask again, now, what this machine is. Used when the server has just refused
     *  something in a way that says this screen's picture of itself is stale — a
     *  kiosk told the branch is closed a few seconds after it thought it was open. */
    reidentify,
    resync: () => connection.current?.resync(),
    reconnect: () => connection.current?.reconnect(),
    report,
    reportPrinter,
  }
}

function errorText(fault: unknown, lang: StaffLanguage): string {
  if (fault === null) return ''
  if (fault === NO_BRANCH) return t('common.noBranchAvailable', lang)
  return explain(fault, lang, 'common.unreachable')
}
